import { ConfirmationModal, Text, useModal } from '@procurify/ui'

import { toast } from '@webapp/components/Notification'
import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { useMutateDeletePOCustomField } from '@webapp/modules/purchasing/features/delete-po-custom-field/models'
import messages from './messages'

interface UseDeletePOCustomFieldPresenterOptions {
  onSuccess?: () => void
}

/**
 * Danger confirmation + DELETE for one PO custom field. The programmatic
 * `ConfirmationModal` replaces the Angular hold-to-delete anti-pattern
 * (BHV-015); the row disappears via list refetch, not a local splice.
 */
export const useDeletePOCustomFieldPresenter = (
  options: UseDeletePOCustomFieldPresenterOptions = {}
) => {
  const intl = useProcurifyIntl()
  const modal = useModal()
  const { mutateAsync, isLoading } = useMutateDeletePOCustomField()

  const deleteField = (field: CustomField) => {
    modal.showModal(ConfirmationModal, {
      open: true,
      icon: 'attention',
      type: 'danger',
      header: intl.formatMessage(messages.confirmationModalTitle),
      content: (
        <Text>
          {intl.formatMessage(messages.confirmationModalDescription, {
            name: (
              <Text bold inline>
                {field.name}
              </Text>
            ),
          })}
        </Text>
      ),
      confirmButtonText: intl.formatMessage(
        messages.confirmationModalConfirmButton
      ),
      cancelButtonText: intl.formatMessage(
        messages.confirmationModalCancelButton
      ),
      handleDismiss: () => {
        // Cancel makes no API call (BHV-015)
        modal.hideModal()
      },
      onConfirm: async (done: () => void) => {
        try {
          await mutateAsync(field.id)

          toast.success({
            title: intl.formatMessage(messages.successTitle),
            children: intl.formatMessage(messages.successMessage),
          })

          modal.hideModal()
          options.onSuccess?.()
        } catch (error) {
          const errorMessage =
            getMessageFromError(error) ||
            intl.formatMessage(messages.defaultErrorMessage)

          toast.error({
            title: intl.formatMessage(messages.errorTitle),
            children: errorMessage,
          })

          // Keep the modal open/retryable on failure
          done()
        }
      },
    })
  }

  return {
    commands: {
      deleteField,
    },
    states: {
      isDeleting: isLoading,
    },
  }
}
